/**
 * Prototype-only: the import wizard's endpoints, answered in the browser.
 *
 * The point of this prototype is the unhappy path, so every handler here reads
 * the scenario frozen onto the batch at upload time and fails where the real
 * backend would. Timings are short (a couple of seconds per phase) so the
 * spinners are visible without being a wait.
 */
import { jsonResponse, registerDevMockRoutes } from "@/lib/dev-mode";
import { CLEAN_PREVIEW, IMPORT_SUMMARY, ROW_ERRORS_PREVIEW, VENDORS } from "./fixtures";
import { REVENUE_ERROR } from "./revenueFixtures";
import { createImportedStatement, resetRevenueMock } from "./revenueMock";
import "./revenueMock"; // registers the revenue + catalogue routes
import { getScenario, isCatalogFixed, type ImportScenario } from "./scenario";

const BATCH_KEY = "rie-batch";
const RESOLVED_KEY = "rie-resolved";
const PHASE_KEY = "rie-phase-ms";

/** How long each phase takes. The smoke test shortens it through PHASE_KEY. */
function phaseMs(): number {
  const override = Number(localStorage.getItem(PHASE_KEY));
  return override > 0 ? override : 2200;
}

interface MockBatch {
  id: string;
  label: string;
  name: string | null;
  vendor: string | null;
  file_name: string | null;
  file_size: number | null;
  status: string;
  progress: number;
  counters: Record<string, number> | null;
  error: string | null;
  created_at: string;
  updated_at: string;
  /** Frozen at upload: switching the dial mid-run must not rewrite this run. */
  scenario: ImportScenario;
  phase_start: number;
}

function loadBatch(): MockBatch | null {
  try {
    const raw = localStorage.getItem(BATCH_KEY);
    return raw ? (JSON.parse(raw) as MockBatch) : null;
  } catch {
    return null;
  }
}

function saveBatch(batch: MockBatch): void {
  try {
    localStorage.setItem(BATCH_KEY, JSON.stringify(batch));
  } catch {
    /* the run still works for this page view */
  }
}

function loadResolved(): string[] {
  try {
    const raw = localStorage.getItem(RESOLVED_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function markResolved(id: string): void {
  try {
    const all = new Set(loadResolved());
    all.add(id);
    localStorage.setItem(RESOLVED_KEY, JSON.stringify([...all]));
  } catch {
    /* the drawer still closes; the row just comes back */
  }
}

/** Drop the batch so the wizard starts from the upload form again. */
export function resetPrototype(): void {
  try {
    localStorage.removeItem(BATCH_KEY);
    localStorage.removeItem(RESOLVED_KEY);
  } catch {
    /* nothing to clear */
  }
  resetRevenueMock();
}

function wireBatch(batch: MockBatch): object {
  const { scenario: _scenario, phase_start: _phaseStart, ...rest } = batch;
  return rest;
}

function fileNameFrom(body: BodyInit | null | undefined): string {
  if (body instanceof FormData) {
    const file = body.get("file");
    if (file instanceof File) return file.name;
    if (typeof file === "string" && file) return file;
  }
  return "revenue_statement.csv";
}

function fieldFrom(body: BodyInit | null | undefined, key: string): string | null {
  if (body instanceof FormData) {
    const value = body.get(key);
    if (typeof value === "string" && value) return value;
  }
  return null;
}

function createBatch(body: BodyInit | null | undefined): MockBatch {
  const now = new Date();
  const batch: MockBatch = {
    id: crypto.randomUUID(),
    label: fieldFrom(body, "label") ?? "dev-label",
    name: fieldFrom(body, "name"),
    vendor: fieldFrom(body, "vendor"),
    file_name: fileNameFrom(body),
    file_size: 248_320,
    status: "analyzing",
    progress: 0,
    counters: null,
    // The real backend fills this in on a failure. The UI never shows it — that
    // is one of the gaps this prototype is here to make visible.
    error: null,
    created_at: now.toISOString(),
    updated_at: now.toISOString(),
    scenario: getScenario(),
    phase_start: now.getTime(),
  };
  saveBatch(batch);
  return batch;
}

/** Where "analyzing" ends up for a scenario. */
function analysisOutcome(scenario: ImportScenario): string {
  if (scenario === "analysis-failed") return "failed";
  if (scenario === "canceled") return "canceled";
  return "analyzed";
}

function advanceByTime(batch: MockBatch): MockBatch {
  const elapsed = Date.now() - batch.phase_start;
  if (elapsed < phaseMs()) return batch;

  if (batch.status === "analyzing") {
    batch.status = analysisOutcome(batch.scenario);
    batch.progress = 100;
    batch.error =
      batch.status === "failed"
        ? "Row 41: column 'Net Revenue' missing after row 40; the file changes shape mid-way."
        : null;
    batch.updated_at = new Date().toISOString();
    batch.phase_start = Date.now();
    saveBatch(batch);
  } else if (batch.status === "importing") {
    batch.status = "completed";
    batch.progress = 100;
    batch.updated_at = new Date().toISOString();
    batch.phase_start = Date.now();
    saveBatch(batch);
  }
  return batch;
}

/**
 * Conflicts the reviewer has already merged drop out of the preview, the way
 * the backend would stop returning them. Rows in `errors` never drop out —
 * there is no UI that resolves one, which is the gap worth seeing.
 */
function previewFor(scenario: ImportScenario) {
  // "Release without tracks" only goes wrong after Save import, so everything
  // up to it is the clean baseline.
  if (scenario !== "row-errors") return CLEAN_PREVIEW;
  const resolved = new Set(loadResolved());
  const conflicts_releases = ROW_ERRORS_PREVIEW.conflicts_releases.filter(
    (item) => !resolved.has(item.id),
  );
  const conflicts_recordings = ROW_ERRORS_PREVIEW.conflicts_recordings.filter(
    (item) => !resolved.has(item.id),
  );
  return {
    ...ROW_ERRORS_PREVIEW,
    conflicts_releases,
    conflicts_recordings,
    summary: {
      ...ROW_ERRORS_PREVIEW.summary,
      conflicts_releases: conflicts_releases.length,
      conflicts_recordings: conflicts_recordings.length,
    },
  };
}

registerDevMockRoutes((request, url) => {
  const path = url.pathname.replace(/\/$/, "");
  const { method, body } = request;

  if (method === "GET" && path.endsWith("/imports/vendors")) {
    return jsonResponse(VENDORS);
  }

  if (method === "POST" && path.endsWith("/imports/batches")) {
    const scenario = getScenario();
    if (scenario === "upload-rejected") {
      // DRF field error: the app surfaces it under the dropzone via extractFieldErrors.
      return jsonResponse(
        {
          file: [
            "This file does not match the Spotify statement layout: expected a 'Net Revenue' column.",
          ],
        },
        400,
      );
    }
    if (scenario === "upload-500") {
      return jsonResponse({ detail: "Internal server error." }, 500);
    }
    return jsonResponse(wireBatch(createBatch(body)), 201);
  }

  const batchMatch = path.match(/\/imports\/batches\/([^/]+)(\/[^/]+)?$/);
  if (batchMatch) {
    const [, id, sub] = batchMatch;
    const batch = loadBatch();
    if (!batch || batch.id !== id) return jsonResponse({ detail: "Not found." }, 404);

    if (!sub && method === "GET") {
      return jsonResponse(wireBatch(advanceByTime(batch)));
    }

    if (sub === "/catalog-preview" && method === "GET") {
      if (batch.scenario === "preview-failed") {
        return jsonResponse({ detail: "Preview could not be built." }, 500);
      }
      return jsonResponse(previewFor(batch.scenario));
    }

    if (sub === "/confirm" && method === "POST") {
      if (batch.scenario === "confirm-failed") {
        return jsonResponse({ detail: "Import could not be started. Try again." }, 500);
      }
      // The catalog part lands; the statement it produced carries whatever the
      // revenue calculation made of it.
      if (batch.scenario === "release-without-tracks") {
        createImportedStatement(batch.id, batch.file_name);
      }
      batch.status = "importing";
      batch.progress = 0;
      batch.phase_start = Date.now();
      batch.updated_at = new Date().toISOString();
      saveBatch(batch);
      return jsonResponse(null, 204);
    }

    if (sub === "/summary" && method === "GET") {
      if (batch.scenario === "release-without-tracks" && !isCatalogFixed()) {
        // Catalog counts are real; the revenue line count is not, because the
        // calculation that would have produced it gave up.
        return jsonResponse({ ...IMPORT_SUMMARY, revenue_lines: 0, revenue_error: REVENUE_ERROR });
      }
      return jsonResponse(IMPORT_SUMMARY);
    }
  }

  // Resolving a conflict in the merge drawer: accept whichever side was picked.
  const resolution = path.match(/\/imports\/items\/([^/]+)\/resolution$/);
  if (resolution && method === "PATCH") {
    markResolved(resolution[1]);
    return jsonResponse({
      id: resolution[1],
      match_status: "exact",
      matched_object_id: null,
      matched_object_type: null,
      recording_resolution: "use_import",
      release_resolution: "use_import",
    });
  }

  return null;
});
